import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

const headerClassMap = {
  primary: `
    bg-[#1e2a74] dark:bg-blue-900/40 
    text-white dark:text-acs-dark-text
    px-fluid-2 py-fluid-1.5 cursor-pointer select-none 
    flex items-center justify-between transition-none
  `,
  secondary: `
    bg-[#423A91] dark:bg-indigo-900/40 
    text-white dark:text-acs-dark-text
    px-fluid-2 py-fluid-1.5 cursor-pointer select-none 
    flex items-center justify-between transition-none
  `,
  tertiary: `
    bg-[#5B65A5] dark:bg-violet-900/40 
    text-white dark:text-acs-dark-text
    px-fluid-2 py-fluid-1.5 cursor-pointer select-none 
    flex items-center justify-between transition-none
  `,
  success: `
    bg-[#31C4AB] dark:bg-emerald-900/40 
    text-black dark:text-emerald-200 
    px-fluid-2 py-fluid-1.5 cursor-pointer select-none 
    flex items-center justify-between transition-none
  `,
  info: `
    bg-[#232849] dark:bg-slate-900/40 
    text-white dark:text-acs-dark-text 
    px-fluid-2 py-fluid-1.5 cursor-pointer select-none 
    flex items-center justify-between transition-none
  `,
};

const headerBgColorMap = {
  primary: "primary-bg dark:acs-dark-surface",
  secondary: "secondary-bg dark:acs-dark-surface",
  tertiary: "tertiary-bg dark:acs-dark-surface",
  success: "success-bg dark:acs-dark-surface",
  info: "info-bg dark:acs-dark-surface",
};

const getHeaderBgColor = (type = "primary") =>
  headerBgColorMap[type] || "primary-bg dark:acs-dark-surface";

export const ExpandableHeader = ({
  headerSection,
  headerLabel,
  isExpanded,
  onToggle,
  initiallyExpanded = true,
  count,
  icon: Icon,
  headerType = "primary",
  children,
  rightSection, 
  disableToggle = false, 
  allowOverflow = false,
  // ✅ optional edit icon or action button
}) => {
  const isControlled = typeof isExpanded === "boolean";
  const [internalExpanded, setInternalExpanded] = useState(initiallyExpanded);
  const expanded = isControlled ? isExpanded : internalExpanded;

  const handleToggle = () => {
    if (isControlled) onToggle?.();
    else setInternalExpanded((prev) => !prev);
  };

  const headerClass = headerClassMap[headerType] || headerClassMap.primary;
  const bgColor = getHeaderBgColor(headerType);

  return (
    <div
      className={`border border-gray-300 rounded-md dark:border-acs-dark-border mb-fluid-3 bg-white dark:bg-acs-dark-surface ${
        allowOverflow ? "overflow-visible" : "overflow-hidden"
      }`}
      style={{ backgroundColor: expanded ? bgColor : undefined }}
    >
      {/* === Header === */}
       <div
          onClick={!disableToggle ? handleToggle : undefined}
          className={`${headerClass} w-full text-left text-fluid-base font-medium ${
            disableToggle ? "cursor-default" : "cursor-pointer"
          }`}
          role={!disableToggle ? "button" : undefined}
          tabIndex={!disableToggle ? 0 : undefined}
        >


        <div className="flex items-center justify-between w-full">
          {/* === Left Section === */}
          <span className="inline-flex items-center gap-fluid-2">
           {!disableToggle && (
              <ChevronDown
                className={`w-fluid-icon-sm h-fluid-icon-sm transition-transform duration-300 ${
                  expanded ? "rotate-180" : "rotate-0"
                }`}
              />
            )}

            {Icon && <Icon className="w-fluid-icon-sm h-fluid-icon-sm" />}
            {headerSection && (
              <span className="font-roboto font-semibold">{headerSection}</span>
            )}
            {headerLabel && (
              <span className="font-roboto font-medium">{headerLabel}</span>
            )}
          </span>

          {/* === Right Section === */}
          <div className="flex items-center gap-fluid-2 ml-auto">
            {typeof count === "number" && (
              <span
                className={`px-fluid-2.75 py-[0.3rem] text-fluid-sm rounded-md font-medium 
                  ${
                    headerType === "primary" || headerType === "success"
                      ? "bg-white text-white dark:bg-[#3B5CCC]/25 dark:text-acs-dark-text"
                      : "bg-[#E6E8F0] dark:bg-acs-dark-hover text-gray-700 dark:text-acs-dark-text"
                  }`}
              >
                {count}
              </span>
            )}
            {rightSection && (
              <div
                onClick={(e) => e.stopPropagation()}
                className="flex items-center"
              >
                {rightSection}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* === Expandable Content === */}
      {expanded && (
        <div className="bg-white dark:bg-acs-dark-surface-2 text-gray-800 dark:text-acs-dark-text">
          {children}
        </div>
      )}
    </div>
  );
};
